/**
 * Sizing rules shared by the three import preview grids (customers, articles,
 * special prices).
 *
 * The grids used to get a fixed height - 610px alone, 250px when another grid
 * had data - which ignored how many rows they actually hold: a file with three
 * customer rows still rendered a 610px grid, most of it blank. The height is now
 * derived from the rows on screen, and only the ROW BUDGET depends on how many
 * grids share the tab.
 *
 * Row budget: the collapsed grids together show about 16 rows, so one grid gets
 * 16, two get 8 each and three get 5 each - enough for every grid in the file to
 * stay visible without scrolling past a tall one, and never fewer than 3 rows.
 */

const ROW_HEIGHT = 32; // Table's rowHeight
const COLUMN_HEADER_HEIGHT = 36; // Table's columnHeaderHeight
const FOOTER_HEIGHT = 36; // the Footer element each grid renders
const BORDERS = 2;

const GRID_CHROME_HEIGHT = COLUMN_HEADER_HEIGHT + FOOTER_HEIGHT + BORDERS;

/** Rows an expanded grid shows before it starts scrolling inside itself. */
export const MAX_VISIBLE_ROWS = 16;

const MIN_COLLAPSED_ROWS = 3;
const COLLAPSED_ROW_BUDGET = 16;

/** Rows a collapsed grid shows, given how many grids the file filled. */
export const collapsedVisibleRows = (gridsWithData: number): number =>
  Math.max(MIN_COLLAPSED_ROWS, Math.floor(COLLAPSED_ROW_BUDGET / Math.max(1, gridsWithData)));

/**
 * Rows to render right now. Expanding does not grow the page without limit: past
 * MAX_VISIBLE_ROWS the grid keeps its height and scrolls internally, so a
 * 500-row file cannot turn the tab into a 16000px page.
 */
export const visibleRows = (args: {
  rowCount: number;
  collapsedRows: number;
  isAllDataShown: boolean;
}): number => {
  const { rowCount, collapsedRows, isAllDataShown } = args;
  return Math.min(rowCount, isAllDataShown ? MAX_VISIBLE_ROWS : collapsedRows);
};

/** Grid height that exactly fits `rows` rows - no blank area below the last one. */
export const importGridHeight = (rows: number): number =>
  GRID_CHROME_HEIGHT + Math.max(1, rows) * ROW_HEIGHT;
